/**
 * Whisper-backed continuous microphone — the real "hearing" organ.
 *
 * Replaces the unreliable browser SpeechRecognition path. Captures
 * microphone audio with MediaRecorder, detects speech segments with
 * a level-based VAD, and sends each segment to the backend Whisper
 * transcriber (POST /api/me/voice/listen). Runs continuously until
 * stopped, so the conversation flows turn after turn without extra
 * button presses. While ME is speaking the mic is gated (muted) so
 * it never hears its own voice.
 */
import { api } from "@/lib/api";

const START_LEVEL = 0.045;   // speech onset threshold (0..1)
const KEEP_LEVEL = 0.028;    // sustain threshold while speaking
const SILENCE_MS = 1100;     // silence that ends a segment
const MIN_SPEECH_MS = 400;   // discard blips shorter than this
const MAX_SEGMENT_MS = 15000;

function pickMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  for (const t of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]) {
    try { if (MediaRecorder.isTypeSupported(t)) return t; } catch { /* noop */ }
  }
  return "";
}

export function createWhisperMic({
  language = "tr",
  onLevel = () => {},
  onTranscript = () => {},
  onState = () => {},
  onError = () => {},
}) {
  let stream = null;
  let ctx = null;
  let analyser = null;
  let raf = 0;
  let recorder = null;
  let chunks = [];
  let running = false;
  let capturing = false;
  let muted = false;
  let discardSegment = false;
  let segmentStart = 0;
  let lastVoice = 0;

  function beginSegment() {
    const mime = pickMimeType();
    try {
      recorder = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream);
    } catch (e) {
      onError(e);
      return;
    }
    chunks = [];
    discardSegment = false;
    recorder.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) chunks.push(ev.data);
    };
    recorder.onstop = () => {
      const dur = performance.now() - segmentStart;
      const blob = new Blob(chunks, { type: recorder.mimeType || "audio/webm" });
      chunks = [];
      if (!discardSegment && dur >= MIN_SPEECH_MS && blob.size > 2000) {
        transcribe(blob);
      } else if (running) {
        onState("listening");
      }
    };
    recorder.start();
    capturing = true;
    segmentStart = performance.now();
    lastVoice = segmentStart;
    onState("capturing");
  }

  function endSegment(discard = false) {
    if (!capturing) return;
    capturing = false;
    discardSegment = discard;
    try { recorder.stop(); } catch { /* noop */ }
  }

  async function transcribe(blob) {
    onState("transcribing");
    try {
      const fd = new FormData();
      fd.append("audio", blob, "speech.webm");
      if (language) fd.append("language_hint", language);
      const r = await api.post("/me/voice/listen", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const text = (r.data?.payload?.text || "").trim();
      if (running) onState("listening");
      if (text.length > 1) onTranscript(text);
    } catch (e) {
      onError(e);
      if (running) onState("listening");
    }
  }

  function tick() {
    if (!running || !analyser) return;
    const buf = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i];
    const level = sum / buf.length / 255;
    onLevel(level);

    const now = performance.now();
    if (muted) {
      // ME is speaking — never capture our own voice.
      if (capturing) endSegment(true);
    } else if (!capturing) {
      if (level >= START_LEVEL) beginSegment();
    } else {
      if (level >= KEEP_LEVEL) lastVoice = now;
      if (now - lastVoice >= SILENCE_MS) endSegment(false);
      else if (now - segmentStart >= MAX_SEGMENT_MS) endSegment(false);
    }
    raf = requestAnimationFrame(tick);
  }

  return {
    async start() {
      if (running) return;
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(stream);
      analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      running = true;
      onState("listening");
      raf = requestAnimationFrame(tick);
    },
    stop() {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      if (capturing) endSegment(true);
      if (stream) stream.getTracks().forEach((t) => t.stop());
      if (ctx) try { ctx.close(); } catch { /* noop */ }
      stream = null; ctx = null; analyser = null;
      onLevel(0);
      onState("idle");
    },
    setMuted(v) { muted = Boolean(v); },
    isRunning() { return running; },
  };
}
